import { ApiErrorBody, ERROR_CODES, type ErrorCode } from '@seans/contracts';

/** Failures detected by the client itself, before or instead of an API answer. */
export const CLIENT_ERROR_CODES = ['network', 'timeout', 'invalid_response'] as const;
export type ClientErrorCode = (typeof CLIENT_ERROR_CODES)[number];
export type ApiErrorCode = ErrorCode | ClientErrorCode;

/**
 * Which failures are worth retrying automatically. Validation, auth and conflict answers are
 * final; transport problems, throttling and server faults may pass.
 */
export const ERROR_TRANSIENCE = {
  unauthenticated: false,
  forbidden: false,
  not_found: false,
  validation_failed: false,
  rate_limited: true,
  credentials_invalid: false,
  account_disabled: false,
  login_taken: false,
  invitation_invalid: false,
  invite_invalid: false,
  screening_ended: false,
  participant_banned: false,
  admission_pending: false,
  admission_denied: false,
  admission_decision_conflict: false,
  playback_version_conflict: false,
  asset_not_ready: false,
  asset_in_use: false,
  track_not_deletable: false,
  upload_too_large: false,
  unsupported_subtitle_format: false,
  unsupported_image_format: false,
  realtime_unavailable: true,
  internal: true,
  network: true,
  timeout: true,
  invalid_response: false,
} as const satisfies Record<ApiErrorCode, boolean>;

export class ApiError extends Error {
  readonly code: ApiErrorCode;
  /** HTTP status, or null when no response arrived. */
  readonly status: number | null;
  readonly details: unknown;

  constructor(
    code: ApiErrorCode,
    message: string,
    options: { status?: number | null; details?: unknown; cause?: unknown } = {},
  ) {
    super(message, { cause: options.cause });
    this.name = 'ApiError';
    this.code = code;
    this.status = options.status ?? null;
    this.details = options.details;
  }

  get transient(): boolean {
    return ERROR_TRANSIENCE[this.code];
  }
}

export const isApiError = (error: unknown): error is ApiError => error instanceof ApiError;

export const hasErrorCode = (error: unknown, code: ApiErrorCode): boolean =>
  isApiError(error) && error.code === code;

const SERVER_CODES: ReadonlySet<string> = new Set(ERROR_CODES);
export const isServerErrorCode = (code: string): code is ErrorCode => SERVER_CODES.has(code);

/**
 * Answers that the HTTP layer in front of the API (the proxy) gives on its own, without the API's
 * error body: they still mean something specific to people.
 */
const PROXY_STATUS_CODES: Readonly<Partial<Record<number, ApiErrorCode>>> = {
  413: 'upload_too_large',
  429: 'rate_limited',
};

/** Converts a non-2xx response body into an ApiError, tolerating bodies that break the contract. */
export const errorFromResponse = (status: number, body: unknown): ApiError => {
  const parsed = ApiErrorBody.safeParse(body);
  if (parsed.success) {
    const { code, message, details } = parsed.data.error;
    return new ApiError(code, message, { status, details });
  }
  const code = PROXY_STATUS_CODES[status] ?? (status >= 500 ? 'internal' : 'invalid_response');
  return new ApiError(code, `HTTP ${status} without an error body`, { status });
};
